import { FileText, X } from "lucide-react";

import type { RecentAsset } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

interface Props {
  asset: RecentAsset;
  /** [View] CTA — opens the asset (or the Assets Hub). */
  onView: () => void;
  /**
   * Non-destructive [X]: takes the card off the feed for this session. There
   * is no server-side dismissal for "new collateral" cards (no per-user
   * seen-marker), so this is a client-side session dismiss only.
   */
  onDismiss: () => void;
}

/**
 * "New to knowledge base" feed card (follow-up #62, info tier). Surfaces a
 * recently-published asset with a [View] CTA into the Assets Hub.
 * The whole card is clickable (its primary action is to view the asset); the
 * [X] dismiss + the explicit button stop propagation so they don't double-fire.
 */
export function NewCollateralCard({ asset, onView, onDismiss }: Props) {
  return (
    <Card
      role="button"
      tabIndex={0}
      aria-label={`View new collateral: ${asset.title}`}
      onClick={onView}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onView();
        }
      }}
      data-testid={`feed-asset-${asset.id}`}
      className="relative cursor-pointer transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Button
        size="sm"
        variant="ghost"
        className="absolute right-2 top-2 h-6 w-6 p-0 text-muted-foreground"
        aria-label={`Dismiss new collateral notification: ${asset.title}`}
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
      >
        <X className="h-4 w-4" aria-hidden />
      </Button>
      <CardContent className="flex flex-col gap-3 p-4 pr-10 sm:flex-row sm:items-center sm:justify-between">
        <div className="grid gap-1">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" aria-hidden />
            <span className="text-sm font-medium">New to knowledge base</span>
            {asset.format ? <Badge variant="outline">{asset.format}</Badge> : null}
          </div>
          <p className="text-sm text-muted-foreground">{asset.title}</p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            data-testid={`feed-asset-view-${asset.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onView();
            }}
          >
            View
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
